import { Injectable } from '@angular/core';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { BehaviorSubject, EMPTY, Observable, ReplaySubject } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { HuntService } from '../../../shared/services/hunt.service';

export interface ExtendedState extends State {
  archived: boolean;
}
export const DEFAULT_STATE: ExtendedState = {
  skip: 0,
  take: 10,
  sort: [{ field: 'created', dir: 'desc' }],
  filter: null,
  archived: false,
};

export enum HuntFilterFields {
  CREATED = 'created',
  ENVIRONMENTS = 'environments',
  ASSIGNEE = 'assignee',
  AUTHOR = 'author',
  STATUS = 'status',
  PRIORITY = 'priority',
  OUTCOME = 'outcome',
  ID = 'id',
  HUNT_TEMPLATE = 'huntTemplate',
  ARCHIVED = 'archived',
}

@Injectable()
export class HuntStateService {
  /**
   * The complete current hunt state
   * Private internal state observable hooked back to public BehaviorSubject for .value compat throughout
   */

  public huntState: BehaviorSubject<ExtendedState> = new BehaviorSubject({
    ...DEFAULT_STATE,
  });

  /**
   * The hunt state results based on the above state, piped alongside a loading indicator subject set to true when new results are being retrieved and false when up to date
   */
  public resultsLoading: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  public resultsError: ReplaySubject<Error> = new ReplaySubject<Error>(1);
  public results: Observable<GridDataResult> = this.huntState.pipe(
    tap(() => {
      this.resultsLoading.next(true);
      this.resultsError.next(null);
    }),
    switchMap((state) => {
      return this.huntService.getHunts(state).pipe(
        catchError((e: Error) => {
          console.error(e);
          this.resultsError.next(e);
          return EMPTY;
        }),
        map((huntList) => {
          const temp: GridDataResult = { data: [], total: 0 };
          temp.data = huntList.items;
          temp.total = huntList.meta.totalItems;
          return temp;
        }),
      );
    }),
    tap(() => this.resultsLoading.next(false)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor(private huntService: HuntService) {}

  /**
   * Sets the entire state to the passed object, defaulting any unprovided values back to app defaults.
   * Will only update external state if there are changes between the passed state and the current search state.
   *
   * @param state
   */
  public setState(state: Partial<ExtendedState>): void {
    this.updateState({ ...DEFAULT_STATE, ...state });
  }

  /**
   * Reset the search
   */
  public reset(): void {
    this.updateState(DEFAULT_STATE);
  }

  public updateState(newState?: Partial<ExtendedState>): void {
    this.huntState.next({
      ...this.huntState.value,
      ...newState,
    });
  }
}
